import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Scales, Info } from '@phosphor-icons/react';
import { COLLEGES } from '../constants/colleges';
import { PageHero } from '../components/motion/PageHero';

export default function VoteSimulationPage() {
  // % de voix POUR par collège (0 à 100)
  const [pctPour, setPctPour] = useState(Object.fromEntries(COLLEGES.map(c => [c.value, 50])));
  // Optionnel : % de participation par collège
  const [participation, setParticipation] = useState(Object.fromEntries(COLLEGES.map(c => [c.value, 100])));

  const rows = COLLEGES.map(c => {
    const p = parseFloat(pctPour[c.value] ?? 0) || 0;
    const part = parseFloat(participation[c.value] ?? 100) || 0;
    // Contribution pondérée = ponderation × (part/100) × (p/100)
    const contribution = (c.ponderation * part * p) / 10000;
    const contributionMax = (c.ponderation * part) / 100;
    return { ...c, p, part, contribution, contributionMax };
  });
  const totalPour = rows.reduce((s, r) => s + r.contribution, 0);
  const totalPossible = rows.reduce((s, r) => s + r.contributionMax, 0);
  const scorePondere = totalPossible > 0 ? (totalPour / totalPossible) * 100 : 0;
  const adopted = scorePondere >= 50;

  const reset = () => {
    setPctPour(Object.fromEntries(COLLEGES.map(c => [c.value, 0])));
    setParticipation(Object.fromEntries(COLLEGES.map(c => [c.value, 100])));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vote-simulation-page">
      <PageHero
        title="Simulation vote AG"
        subtitle="Calcul pondéré multi-collège selon les statuts SCIC (art. 23) — A=30% · E=20% · B/C/D/F/G=10%"
        icon={<Scales size={24} weight="duotone" className="text-white" />}
        gradient="from-[#2D5A4C] to-[#1E4136]"
        accents={["#80C51F", "#00AEEF"]}
      />

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs font-semibold text-[#5E6D66] uppercase pb-2 border-b">
            <div>Collège</div>
            <div>Participation (%)</div>
            <div>Voix POUR (%)</div>
            <div className="text-right">Contribution pondérée</div>
          </div>
          {rows.map((r) => (
            <div key={r.value} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center py-2 border-b border-[#F1EFE9] last:border-0" data-testid={`vote-row-${r.value}`}>
              <div className="flex items-center gap-2">
                <r.Icon size={16} style={{ color: r.color }} />
                <span className="font-semibold">{r.value}</span>
                <span className="text-xs text-[#5E6D66] truncate">{r.label}</span>
                <Badge variant="outline" className="text-[10px]">{r.ponderation}%</Badge>
              </div>
              <Input
                type="number" min={0} max={100} value={r.part}
                onChange={(e) => setParticipation({ ...participation, [r.value]: e.target.value })}
                className="h-9" data-testid={`participation-${r.value}`}
              />
              <Input
                type="number" min={0} max={100} value={r.p}
                onChange={(e) => setPctPour({ ...pctPour, [r.value]: e.target.value })}
                className="h-9" data-testid={`pctpour-${r.value}`}
              />
              <div className="text-right tabular-nums text-sm">
                <span className="font-medium text-[#1A2421]">{r.contribution.toFixed(2)}</span>
                <span className="text-[#5E6D66]"> / {r.contributionMax.toFixed(2)} pts</span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-wide text-[#5E6D66]">Score pondéré POUR</p>
            <p className="text-3xl font-bold text-[#1A2421] tabular-nums" data-testid="score-total">
              {scorePondere.toFixed(1)} %
            </p>
            <p className="text-xs text-[#5E6D66] mt-1">{totalPour.toFixed(2)} / {totalPossible.toFixed(2)} pts de pondération</p>
          </CardContent>
        </Card>
        <Card className={`border-2 ${adopted ? 'border-emerald-300 bg-emerald-50' : 'border-rose-300 bg-rose-50'}`}>
          <CardContent className="p-5 flex items-center gap-3">
            {adopted
              ? <CheckCircle size={40} weight="fill" className="text-emerald-600" />
              : <XCircle size={40} weight="fill" className="text-rose-600" />}
            <div>
              <p className="text-xs uppercase tracking-wide text-[#5E6D66]">Résolution</p>
              <p className="text-xl font-bold text-[#1A2421]" data-testid="vote-result">
                {adopted ? 'Adoptée' : 'Rejetée'}
              </p>
              <p className="text-xs text-[#5E6D66]">Seuil requis : 50 %</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-5 flex items-center gap-3">
            <Info size={20} className="text-[#5E6D66]" />
            <div className="text-xs text-[#5E6D66]">
              Simulation indicative. Pour les votes à majorité qualifiée (2/3), ajuster le seuil dans le règlement intérieur.
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={reset} data-testid="vote-reset">Réinitialiser</Button>
        <Button
          variant="outline"
          onClick={() => setPctPour(Object.fromEntries(COLLEGES.map(c => [c.value, 100])))}
          data-testid="vote-all-pour"
        >Tout POUR</Button>
      </div>
    </div>
  );
}
